'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface ExportOption {
  id: string;
  format: string;
  label: string;
  description: string;
  icon: string;
}

const DataExportPanel = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState<string>('pdf');
  const [isExporting, setIsExporting] = useState(false);

  useState(() => {
    setIsHydrated(true);
  });

  const exportOptions: ExportOption[] = [
    {
      id: 'pdf',
      format: 'pdf',
      label: 'PDF Report',
      description: 'Comprehensive report with charts and analysis',
      icon: 'DocumentTextIcon'
    },
    {
      id: 'csv',
      format: 'csv',
      label: 'CSV Data',
      description: 'Raw data for spreadsheet analysis',
      icon: 'TableCellsIcon'
    },
    {
      id: 'json',
      format: 'json',
      label: 'JSON Data',
      description: 'Structured data for developers',
      icon: 'CodeBracketIcon'
    },
    {
      id: 'excel',
      format: 'excel',
      label: 'Excel Workbook',
      description: 'Formatted data with multiple sheets',
      icon: 'DocumentChartBarIcon'
    }
  ];

  const handleExport = () => {
    if (!isHydrated) return;
    
    setIsExporting(true);
    setTimeout(() => {
      setIsExporting(false);
      alert(`Exporting data as ${selectedFormat.toUpperCase()}...`);
    }, 1500);
  };

  if (!isHydrated) {
    return (
      <div className="bg-card border border-border rounded-md p-6">
        <div className="h-8 w-48 bg-muted rounded-md animate-pulse mb-4"></div>
        <div className="space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-16 bg-muted rounded-md animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-md p-4 lg:p-6 shadow-organic-sm">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-headline font-bold text-foreground">Export Data</h3>
        <Icon name="ArrowDownTrayIcon" size={20} variant="outline" className="text-primary" />
      </div>

      <div className="space-y-3 mb-6">
        {exportOptions.map((option) => (
          <button
            key={option.id}
            onClick={() => setSelectedFormat(option.format)}
            className={`w-full p-4 rounded-md border text-left transition-organic duration-300 ${
              selectedFormat === option.format
                ? 'bg-primary/10 border-primary' :'bg-background border-border hover:border-primary/50'
            }`}
          >
            <div className="flex items-start space-x-3">
              <div className={`p-2 rounded-md ${selectedFormat === option.format ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
                <Icon name={option.icon as any} size={20} variant="outline" />
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-headline font-bold text-foreground mb-1">{option.label}</h4>
                <p className="text-xs font-body text-muted-foreground">{option.description}</p>
              </div>
              {selectedFormat === option.format && (
                <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-primary flex-shrink-0" />
              )}
            </div>
          </button>
        ))}
      </div>

      <button
        onClick={handleExport}
        disabled={isExporting}
        className="w-full flex items-center justify-center space-x-2 px-6 py-3 bg-primary text-primary-foreground font-cta text-sm font-semibold rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed shadow-organic-sm transition-organic duration-300"
      >
        {isExporting ? (
          <>
            <Icon name="ArrowPathIcon" size={20} variant="outline" className="animate-spin" />
            <span>Exporting...</span>
          </>
        ) : (
          <>
            <Icon name="ArrowDownTrayIcon" size={20} variant="outline" />
            <span>Export as {selectedFormat.toUpperCase()}</span>
          </>
        )}
      </button>

      <div className="mt-4 p-3 bg-muted rounded-md">
        <div className="flex items-start space-x-2">
          <Icon name="InformationCircleIcon" size={18} variant="outline" className="text-primary flex-shrink-0 mt-0.5" />
          <p className="text-xs font-body text-muted-foreground">
            Exported data includes all filtered results and respects your current view settings. Large datasets may take a few moments to process.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DataExportPanel;